import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type FileUploadErrorResponse,
  type FileUploadSuccessResponse,
  type StartImportResponse,
} from '../data-layer/types';

export interface DiscoveryFileUploadState {
  loading: boolean;
  error: FileUploadErrorResponse | null;
  data: FileUploadSuccessResponse[];
  uploadedFile: File | null;
  isFileValid: boolean;
  isPreviewTable: boolean;
  isStartImport: boolean;
  criticalCount: number;
  criticalMessage: string;
  updatedHeaders: Record<number, string | null>;
  startImportResponse: StartImportResponse | null;
}

const initialState: DiscoveryFileUploadState = {
  loading: false,
  error: null,
  data: [],
  uploadedFile: null,
  isFileValid: false,
  isPreviewTable: false,
  isStartImport: false,
  criticalCount: 0,
  criticalMessage: '',
  updatedHeaders: [],
  startImportResponse: null,
};

const discoveryFileUploadSlice = createSlice({
  name: 'discoveryFileUpload',
  initialState,
  reducers: {
    setDiscoveryFileUploadLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setDiscoveryFileUploadResult: (
      state,
      action: PayloadAction<FileUploadSuccessResponse[]>
    ) => ({
      ...state,
      data: action.payload,
    }),
    setDiscoveryFileUploadError: (
      state,
      action: PayloadAction<FileUploadErrorResponse | null>
    ) => ({
      ...state,
      error: action.payload,
    }),
    setUploadedFile: (state, action: PayloadAction<File | null>) => ({
      ...state,
      uploadedFile: action.payload,
    }),
    setIsFileValid: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isFileValid: action.payload,
    }),
    setIsPreviewTable: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isPreviewTable: action.payload,
    }),
    setCriticalCount: (state, action: PayloadAction<number>) => ({
      ...state,
      criticalCount: action.payload,
    }),
    setCriticalMessage: (state, action: PayloadAction<string>) => ({
      ...state,
      criticalMessage: action.payload,
    }),
    setUpdatedHeaders: (
      state,
      action: PayloadAction<Record<number, string | null>>
    ) => ({
      ...state,
      updatedHeaders: action.payload,
    }),
    setIsStartImport: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isStartImport: action.payload,
    }),
    setStartImportResponse: (
      state,
      action: PayloadAction<StartImportResponse | null>
    ) => ({
      ...state,
      startImportResponse: action.payload,
    }),
    resetDiscoveryFileUploadState: () => initialState,
  },
});

export const {
  setDiscoveryFileUploadLoading,
  setDiscoveryFileUploadResult,
  setDiscoveryFileUploadError,
  resetDiscoveryFileUploadState,
  setIsFileValid,
  setUploadedFile,
  setIsPreviewTable,
  setCriticalCount,
  setCriticalMessage,
  setUpdatedHeaders,
  setIsStartImport,
  setStartImportResponse,
} = discoveryFileUploadSlice.actions;
export default discoveryFileUploadSlice;
